/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AIHistoryViewer - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file AIHistoryViewer.tsx
 * @version 1.0.0
 * @description Visor de historial completo de interacciones de IA
 * @author WASP Development Team
 * 
 * FUNCIONALIDADES:
 * - Ver historial completo de interacciones
 * - Filtrar por endpoint, fecha, rating, estado
 * - Paginación de resultados
 * - Ver detalle de interacción en modal
 * - Exportar historial (CSV/JSON)
 * - Eliminar entradas individuales
 * - Búsqueda por texto
 * 
 * NOTA: Este componente usa mock data porque el backend aún no tiene
 * endpoints de historial de IA implementados.
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Alert
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faSearch,
  faFilter,
  faEye,
  faTrash,
  faDownload,
  faStar,
  faClock,
  faCoins,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAIEndpoints } from '../../../../../hooks/useAIEndpoints';
import { AIInteraction } from '../../../../../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// MOCK DATA (temporal hasta que backend tenga endpoints)
// ═══════════════════════════════════════════════════════════════════════════

const MOCK_INTERACTIONS: AIInteraction[] = [
  {
    id: 'int_001',
    endpoint_id: 'ep_001',
    user_message: '¿Cuál es la capital de Francia?',
    ai_response: 'La capital de Francia es París.',
    tokens_used: 45,
    response_time: 1200,
    rating: 5,
    status: 'success',
    created_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: 'int_002',
    endpoint_id: 'ep_002',
    user_message: 'Explícame la teoría de la relatividad',
    ai_response: 'La teoría de la relatividad, propuesta por Albert Einstein, consta de dos partes principales...',
    tokens_used: 320,
    response_time: 2800,
    rating: 4,
    status: 'success',
    created_at: new Date(Date.now() - 7200000).toISOString()
  },
  {
    id: 'int_003',
    endpoint_id: 'ep_001',
    user_message: 'Test de conexión',
    ai_response: '',
    tokens_used: 0,
    response_time: 5000,
    status: 'timeout',
    error_message: 'Timeout después de 5 segundos',
    created_at: new Date(Date.now() - 10800000).toISOString()
  }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const AIHistoryViewer: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const { endpoints } = useAIEndpoints();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [interactions, setInteractions] = useState<AIInteraction[]>(MOCK_INTERACTIONS);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEndpoint, setFilterEndpoint] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'success' | 'error' | 'timeout' | ''>('');
  const [filterRating, setFilterRating] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState<AIInteraction | null>(null);

  // ───────────────────────────────────────────────────────────────────────
  // Filtrado y paginación
  // ───────────────────────────────────────────────────────────────────────
  
  const filteredInteractions = useMemo(() => {
    let filtered = [...interactions];

    // Filtrar por búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        int =>
          int.user_message.toLowerCase().includes(searchLower) ||
          int.ai_response.toLowerCase().includes(searchLower)
      );
    }

    // Filtrar por endpoint
    if (filterEndpoint) {
      filtered = filtered.filter(int => int.endpoint_id === filterEndpoint);
    }

    // Filtrar por estado
    if (filterStatus) {
      filtered = filtered.filter(int => int.status === filterStatus);
    }

    // Filtrar por rating
    if (filterRating) {
      filtered = filtered.filter(int => int.rating === filterRating);
    }

    return filtered;
  }, [interactions, searchTerm, filterEndpoint, filterStatus, filterRating]);

  // Paginación
  const totalPages = Math.ceil(filteredInteractions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInteractions = filteredInteractions.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // ───────────────────────────────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────────────────────────────
  
  const handleViewDetail = (interaction: AIInteraction) => {
    setSelectedInteraction(interaction);
    setShowDetailModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta entrada?')) {
      setInteractions(prev => prev.filter(int => int.id !== id));
    }
  };

  const handleExport = (format: 'json' | 'csv') => {
    if (format === 'json') {
      const dataStr = JSON.stringify(filteredInteractions, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-history-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      // CSV
      const headers = ['ID', 'Endpoint', 'Mensaje', 'Respuesta', 'Tokens', 'Tiempo (ms)', 'Rating', 'Estado', 'Fecha'];
      const rows = filteredInteractions.map(int => [
        int.id,
        int.endpoint_id,
        int.user_message.replace(/"/g, '""'),
        int.ai_response.replace(/"/g, '""'),
        int.tokens_used,
        int.response_time,
        int.rating || '',
        int.status,
        new Date(int.created_at).toLocaleString()
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const dataBlob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-history-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Toolbar
  // ───────────────────────────────────────────────────────────────────────
  
  const renderToolbar = () => (
    <Row className="mb-3">
      <Col md={4}>
        <InputGroup>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Buscar en mensajes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </Col>
      <Col md={2}>
        <Form.Select
          value={filterEndpoint}
          onChange={(e) => setFilterEndpoint(e.target.value)}
        >
          <option value="">Todos los endpoints</option>
          {endpoints.map(ep => (
            <option key={ep.id} value={ep.id}>
              {ep.name}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col md={2}>
        <Form.Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
        >
          <option value="">Todos los estados</option>
          <option value="success">Éxito</option>
          <option value="error">Error</option>
          <option value="timeout">Timeout</option>
        </Form.Select>
      </Col>
      <Col md={2}>
        <Form.Select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value ? parseInt(e.target.value) : '')}
        >
          <option value="">Todos los ratings</option>
          <option value="5">⭐⭐⭐⭐⭐</option>
          <option value="4">⭐⭐⭐⭐</option>
          <option value="3">⭐⭐⭐</option>
          <option value="2">⭐⭐</option>
          <option value="1">⭐</option>
        </Form.Select>
      </Col>
      <Col md={2} className="d-flex gap-2">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleExport('json')}
          className="flex-grow-1"
        >
          <FontAwesomeIcon icon={faDownload} /> JSON
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => handleExport('csv')}
          className="flex-grow-1"
        >
          <FontAwesomeIcon icon={faDownload} /> CSV
        </Button>
      </Col>
    </Row>
  );

  // ───────────────────────────────────────────────────────────────────────
  // Render - Tabla
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTable = () => {
    if (filteredInteractions.length === 0) {
      return (
        <Alert variant="info">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          No se encontraron interacciones con los filtros aplicados.
        </Alert>
      );
    }

    return (
      <>
        <Table responsive hover>
          <thead className="bg-light">
            <tr>
              <th>Estado</th>
              <th>Fecha</th>
              <th>Endpoint</th>
              <th>Mensaje</th>
              <th>Métricas</th>
              <th>Rating</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedInteractions.map((interaction) => (
              <tr key={interaction.id}>
                <td>
                  <Badge bg={
                    interaction.status === 'success' ? 'success' :
                    interaction.status === 'error' ? 'danger' : 'warning'
                  }>
                    {interaction.status === 'success' ? (
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                    ) : (
                      <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                    )}
                    {interaction.status}
                  </Badge>
                </td>
                <td>
                  <small className="text-muted">
                    {new Date(interaction.created_at).toLocaleDateString()}<br />
                    {new Date(interaction.created_at).toLocaleTimeString()}
                  </small>
                </td>
                <td>
                  <small className="font-monospace">
                    {endpoints.find(ep => ep.id === interaction.endpoint_id)?.name || interaction.endpoint_id}
                  </small>
                </td>
                <td>
                  <div style={{ maxWidth: 300 }}>
                    <small>
                      {interaction.user_message.length > 80
                        ? interaction.user_message.substring(0, 77) + '...'
                        : interaction.user_message}
                    </small>
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column gap-1">
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {interaction.response_time}ms
                    </small>
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faCoins} className="me-1" />
                      {interaction.tokens_used} tokens
                    </small>
                  </div>
                </td>
                <td>
                  {interaction.rating ? (
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faStar} className="text-warning me-1" />
                      <strong>{interaction.rating}</strong>
                    </div>
                  ) : (
                    <small className="text-muted">N/A</small>
                  )}
                </td>
                <td className="text-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleViewDetail(interaction)}
                    className="me-1"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(interaction.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              
              {[...Array(totalPages)].map((_, idx) => {
                const page = idx + 1;
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <Pagination.Ellipsis key={page} disabled />;
                }
                return null;
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Modal de Detalle
  // ───────────────────────────────────────────────────────────────────────
  
  const renderDetailModal = () => {
    if (!selectedInteraction) return null;

    return (
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Detalle de Interacción
            <Badge bg="secondary" className="ms-2">
              {selectedInteraction.id}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Info general */}
          <Row className="mb-3">
            <Col md={6}>
              <small className="text-muted d-block">Endpoint</small>
              <strong>
                {endpoints.find(ep => ep.id === selectedInteraction.endpoint_id)?.name || selectedInteraction.endpoint_id}
              </strong>
            </Col>
            <Col md={6}>
              <small className="text-muted d-block">Fecha</small>
              <strong>{new Date(selectedInteraction.created_at).toLocaleString()}</strong>
            </Col>
          </Row>

          {/* Métricas */}
          <Row className="mb-3">
            <Col md={3}>
              <small className="text-muted d-block">Estado</small>
              <Badge bg={
                selectedInteraction.status === 'success' ? 'success' :
                selectedInteraction.status === 'error' ? 'danger' : 'warning'
              }>
                {selectedInteraction.status}
              </Badge>
            </Col>
            <Col md={3}>
              <small className="text-muted d-block">Tiempo</small>
              <strong>{selectedInteraction.response_time}ms</strong>
            </Col>
            <Col md={3}>
              <small className="text-muted d-block">Tokens</small>
              <strong>{selectedInteraction.tokens_used}</strong>
            </Col>
            <Col md={3}>
              <small className="text-muted d-block">Rating</small>
              <strong>
                {selectedInteraction.rating ? (
                  <>
                    <FontAwesomeIcon icon={faStar} className="text-warning me-1" />
                    {selectedInteraction.rating}/5
                  </>
                ) : (
                  'N/A'
                )}
              </strong>
            </Col>
          </Row>

          <hr />

          {/* Mensaje */}
          <div className="mb-3">
            <small className="text-muted d-block mb-2">Mensaje del Usuario:</small>
            <div className="p-3 bg-light rounded border">
              {selectedInteraction.user_message}
            </div>
          </div>

          {/* Respuesta */}
          <div className="mb-3">
            <small className="text-muted d-block mb-2">Respuesta de la IA:</small>
            <div className="p-3 bg-light rounded border" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedInteraction.ai_response || <em className="text-muted">Sin respuesta</em>}
            </div>
          </div>

          {/* Error (si existe) */}
          {selectedInteraction.error_message && (
            <Alert variant="danger">
              <strong>Error:</strong> {selectedInteraction.error_message}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div>
      {/* Warning - Mock Data */}
      <Alert variant="warning" className="mb-3">
        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
        <strong>Modo Demo:</strong> Este componente muestra datos de ejemplo. 
        Los endpoints de historial aún no están implementados en el backend.
      </Alert>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faHistory} className="me-2 text-secondary" />
          Historial de Interacciones
          <Badge bg="secondary" className="ms-2">
            {filteredInteractions.length}
          </Badge>
        </h5>
      </div>

      {/* Toolbar */}
      {renderToolbar()}

      {/* Tabla */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {renderTable()}
        </Card.Body>
      </Card>

      {/* Modal */}
      {renderDetailModal()}
    </div>
  );
};

export default AIHistoryViewer;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════